import { ReactElement, useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { GetStaticPropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import dayjs from 'dayjs'
import clsx from 'clsx'
import { SwiperSlide } from 'swiper/react'
import Head from 'next/head'
import { NewsList, NewsType, getNewsList, getNewsHome, TopNewsListAndTypeList } from '@service/news.service'
import { SEOData } from '@service/seo.service'
import retriveMessages, { LocaleType } from '@messages/message'
import { withSEO } from '@common/with-seo'
import { useChinaDetector } from '@common/region-locale'
import { useMobileDetector } from '@hooks/use-mobile-detector'
import { NextPageWithLayout } from '@common/common-types'
import PageLayout from '@components/page-layout/page-layout'
import PdSwiper from '@components/pd-swiper/pd-swiper'
import AngleArrow from '@components/pd-angle-arrow/pd-angle-arrow'
import SlidingTabs from '@components/standard/pd-sliding-tabs'
import Pagination from '@components/standard/pagination'
import PdNews, { NewsMeta } from './pd-news.component'
import { formatDate } from '@/common/date'
// import PdNews, { NewsMeta } from '@components/pd-news/pd-news'

type Pagination = {
  firstPage: number
  endPage: number
  currPage: number
}

type SlidingTabsRef = {
  updatePosition: (newIndex: number, immediate?: boolean, callBack?: Function) => void
}

const News: NextPageWithLayout<{
  initData: TopNewsListAndTypeList
  serverNewsList: NewsMeta[]
  totalNewsCount: number
  defaultPage: number
  seoData?: SEOData | null
}> = ({ initData, serverNewsList, totalNewsCount, defaultPage, seoData }) => {
  const t = useTranslations()
  const { locale, query } = useRouter()
  const inChina = useChinaDetector()
  const isMobileDevice = useMobileDetector()
  const [pageLimit, setPageLimit] = useState<number>(6) // 6 for pc, 4 for mobile
  const [articleType, setArticleType] = useState<NewsType | undefined>(void 0) // '' => all types
  const newsWrapperRef = useRef<HTMLDivElement>(null)
  const slidingTabsRef = useRef<SlidingTabsRef>(null)
  const [articleList, setArticleList] = useState<Array<NewsMeta>>(serverNewsList || [])

  useEffect(() => {
    if (document.documentElement.clientWidth <= 1024) {
      // as mobile device
      setPageLimit(4)
    } else {
      setPageLimit(6)
    }
  }, [])

  const breakpoints = {
    640: {
      slidesPerView: 1,
      spaceBetween: 8,
    },
    768: {
      slidesPerView: 1,
      spaceBetween: 8,
    },
    1024: {
      slidesPerView: 1,
      spaceBetween: 16,
    },
    1280: {
      slidesPerView: 1,
      spaceBetween: 16,
    },
  }

  const [pagination, setPagination] = useState<Pagination>({
    firstPage: 1,
    endPage: Math.ceil(totalNewsCount / 6),
    currPage: defaultPage,
  })

  /* top news */
  const { data: _data, error: topError } = useSWR<TopNewsListAndTypeList>(getNewsHome.swrkey(locale as string), () =>
    getNewsHome(locale as string)
  )
  const topNewsListAndTypeList = _data || initData
  const articleTypeMenus = useMemo(() => topNewsListAndTypeList?.typeList || [], [topNewsListAndTypeList])

  const activeTopNewsType = useMemo<NewsType | undefined>(() => {
    const queryTab = Array.isArray(query.tab) ? query.tab[0] : query.tab
    const hasArticleType = (type?: string): type is NewsType =>
      typeof type === 'string' && articleTypeMenus.some((item) => item.type === type)

    if (hasArticleType(articleType)) {
      return articleType
    }

    if (hasArticleType(queryTab)) {
      return queryTab
    }

    return articleTypeMenus[0]?.type
  }, [articleType, articleTypeMenus, query.tab])

  const topNewsSlides: Array<NewsMeta> = useMemo(() => {
    const topNewsList = topNewsListAndTypeList?.topNewsList || []
    if (!topNewsList.length) {
      return []
    }

    const filteredTopNewsList = activeTopNewsType
      ? topNewsList.filter((article) => article.type === activeTopNewsType)
      : topNewsList

    return filteredTopNewsList.map((article) => ({
      mini: false,
      title: article.title,
      img: article.mediaUrl,
      overview: article.secondTitle,
      link: !article.docId ? article.jumpUrl : `/news/${article.docId}`,
      date: formatDate(article.date, locale),
      type: article.type,
      typeTitle: article.typeTitle,
    }))
  }, [activeTopNewsType, locale, topNewsListAndTypeList])

  useEffect(() => {
    if (typeof articleType !== 'string') return

    getNewsList(
      { offset: (pagination.currPage - 1) * pageLimit, limit: pageLimit },
      articleType,
      locale as string
    ).then((list) => {
      // update pagination
      setPagination((v) => ({ ...v, endPage: Math.ceil(list.count / pageLimit) }))

      // convert article
      const _articleList: Array<NewsMeta> = list.item?.map((article) => ({
        mini: true,
        title: article.title,
        type: article.type,
        typeTitle: article.typeTitle,
        img: article.mediaUrl,
        overview: article.secondTitle,
        link: !article.docId ? article.jumpUrl : `/news/${article.docId}`,
        date: formatDate(article?.date, locale),
        isOutUrl: article.isOutUrl,
        outUrl: article.outUrl,
      }))
      setArticleList(_articleList)
    })
  }, [pagination.currPage, articleType, locale, pageLimit])

  // change article type
  const handleActiveTabChange = (index: number) => {
    setArticleType(articleTypeMenus[index].type)
    setPagination({
      ...pagination,
      currPage: 1,
      endPage: 1,
    })

    !!newsWrapperRef.current && newsWrapperRef.current.scrollIntoView({ behavior: 'smooth' })
  }

  const handlePageChange = (index: number) => {
    setPagination((prev) => ({
      ...prev,
      currPage: index,
    }))

    !!newsWrapperRef.current && newsWrapperRef.current.scrollIntoView({ behavior: 'smooth' })
  }

  const slideContent = useMemo<ReactNode>(() => {
    return (
      <div className={['w-full max-w-300 mx-auto'].join(' ')}>
        <div className="w-full grid gap-8 laptop:gap-4 grid-cols-1 laptop:grid-cols-3">
          {articleList?.map((news, index) => (
            <div key={news.title + news.date + index} className="laptop:h-full h-full overflow-hidden">
              <PdNews
                mini={news.mini}
                showOverview={news.type === 'blog'}
                img={news.img}
                title={news.title}
                overview={news.overview}
                date={news.date}
                type={news.type}
                typeTitle={news.typeTitle}
                link={news.link}
                isOutUrl={news.isOutUrl}
                outUrl={news.outUrl}
                imgClass={'laptop:!h-62 !h-42'}
              />
            </div>
          ))}
        </div>
        <Pagination
          className="mt-8 laptop:mt-12"
          currentPage={pagination.currPage}
          totalPages={pagination.endPage}
          onPageChange={(page) => handlePageChange(page)}
        />
      </div>
    )
  }, [articleList, pagination])

  const initIndex = useMemo<number>(() => {
    const findInitIndex = articleTypeMenus.findIndex((item) => item.type === query['tab'])
    const index = findInitIndex === -1 ? 0 : findInitIndex

    setArticleType(articleTypeMenus?.[index]?.type)
    slidingTabsRef.current?.updatePosition(index)
    return index
  }, [articleTypeMenus, query])

  return (
    <>
      <Head>
        <title>{seoData?.title || topNewsListAndTypeList?.tabTitle || t('Title')}</title>
      </Head>
      <div className={['bg-white', 'laptop:bg-white', 'w-full'].join(' ')}>
        <div className="max-w-300 h-full mx-auto pt-16">
          <h2 className={['text-28 pl-6', 'laptop:text-4xl laptop:pl-0', 'font-semibold'].join(' ')}>
            {topNewsListAndTypeList?.topTitle}
          </h2>

          <div
            className={[
              'bg-pdbg mt-8',
              'laptop:mt-6 laptop:py-8 laptop:max-w-300 laptop:bg-white laptop:border-t border-c2',
              'w-full laptop:h-110',
            ].join(' ')}
          >
            {topNewsSlides?.length > 0 && (
              <PdSwiper
                key={`news-top-${activeTopNewsType || 'all'}`}
                effect="fade"
                fadeEffect={{ crossFade: true }}
                loop={true}
                autoplay={true}
                breakpoints={breakpoints}
                hidePagination={true}
                navgationOptions={
                  isMobileDevice
                    ? {
                        navigationEl: 'pd-navigation h5-new-navigation',
                        nextEl: 'pd-navigation-next h5-new-navigation-next',
                        prevEl: 'pd-navigation-prev h5-new-navigation-prev',
                      }
                    : {
                        navigationEl: 'pd-navigation news-index-navigation',
                        nextEl: 'pd-navigation-next news-index-navigation-next',
                        prevEl: 'pd-navigation-prev news-index-navigation-prev',
                      }
                }
              >
                {topNewsSlides?.map((news, index) => (
                  <SwiperSlide key={news.title + index}>
                    <PdNews
                      img={news.img}
                      title={news.title}
                      overview={news.overview}
                      date={news.date}
                      link={news.link}
                      wfull={false}
                      type={news.type}
                      typeTitle={news.typeTitle}
                    />
                  </SwiperSlide>
                ))}
              </PdSwiper>
            )}
          </div>
        </div>
      </div>

      <div
        id="newsTabs"
        ref={newsWrapperRef}
        className={clsx('mx-auto', 'w-full mt-3 mb-15', 'laptop:w-300 laptop:mt-16 laptop:mb-30')}
      >
        <SlidingTabs
          ref={slidingTabsRef}
          type="dynamic"
          initIndex={initIndex}
          containerClassName="w-full"
          childrenBoxClassName={clsx('px-6', 'laptop:px-0')}
          tabsBoxClassName={clsx(
            'bg-gray-100 sticky top-0 z-20',
            'py-3 laptop:py-6 laptop:justify-start',
            articleTypeMenus.length <= 1 && '!hidden'
          )}
          tabLabels={articleTypeMenus.map((item) => item.title)}
          tabEleClassName={clsx('!py-6px laptop:!py-2')}
          bgRefClassName={clsx('!h-8', 'laptop:!h-10')}
          activeTabChange={handleActiveTabChange}
        >
          {[slideContent].map((content, index) => (
            <div key={index}>{content}</div>
          ))}
        </SlidingTabs>
      </div>
    </>
  )
}

News.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      navbarMoveById="newsTabs"
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}

export default News

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retriveMessages(['news'], locale as LocaleType)
    const initData = await getNewsHome(locale as string)

    // 默认分页为第一页
    const articleType = initData?.typeList?.[0]?.type
    if (typeof articleType !== 'string') {
      // 如果文章类型不是字符串，则返回空列表
      return {
        props: {
          messages,
          initData: initData || {},
          serverNewsList: [],
          totalNewsCount: 0,
          defaultPage: 1,
        },
      }
    }

    const pageLimit = 6
    const currPage = 1

    const list = await getNewsList(
      { offset: (currPage - 1) * pageLimit, limit: pageLimit },
      articleType,
      locale as string
    )
    const serverNewsList: Array<NewsMeta> = list.item?.map((article) => ({
      mini: true,
      title: article.title,
      type: article.type,
      typeTitle: article.typeTitle,
      img: article.mediaUrl,
      overview: article.secondTitle,
      link: !article.docId ? article.jumpUrl : `/news/${article.docId}`,
      date: formatDate(article?.date, locale),
      isOutUrl: article.isOutUrl,
      outUrl: article.outUrl,
    }))

    return {
      props: {
        messages,
        initData: initData || {},
        serverNewsList,
        totalNewsCount: list.count || 0,
        defaultPage: currPage,
        seoData: initData?.seoData,
      },
      revalidate: 60,
    }
  },
  () => '/news'
)
